// Talmia 2.0 · Data Lab · Person detail — comprehensive, organised
// breakout of the raw Person document (Talmia + BC data). Modelled on
// LabProductDetail so the whole Data Lab has a consistent header/panel
// language.
//
// Layout:
//   Header strip (identity + LTV KPI band)
//   LEFT (2 cols) : Contact · Address · Pipedrive linkage · Order IDs · Raw
//   RIGHT (1 col) : Purchase behaviour · Names/Companies · Activities
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, MapPin, TrendingUp, User as UserIcon, Building2,
  GitBranch, Users2, Activity as ActivityIcon, StickyNote, RefreshCw, Loader2,
  ShoppingBag, ExternalLink,
} from "lucide-react";
import { Panel, KV, Chip, BackLink, RawJson, money, dateGB, asText } from "./_shared";
import DataAudit from "./_dataAudit";
import LinkedRecords from "./LinkedRecords";
import PersonHealthZones from "./PersonHealthZones";
import { RecordHistory } from "./v2/_shared";
import PipedriveIdChip from "../../components/PipedriveIdChip";

const TIER_TONE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("platinum")) return "teal";
  if (s.includes("gold"))     return "warn";
  if (s.includes("silver"))   return "slate";
  if (s.includes("bronze"))   return "orange";
  if (s.includes("black"))    return "violet";
  return "slate";
};

export default function LabPersonDetail() {
  const { id } = useParams();
  const [doc, setDoc]   = useState(null);
  const [loading, setL] = useState(true);
  const [err, setErr]   = useState(null);
  const [showAddrs, setShowAddrs] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [orderLinks, setOrderLinks] = useState({}); // number -> { resolved, order_id }

  const load = async () => {
    setL(true);
    try {
      const { data } = await api.get(`/lab/persons/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load person");
    } finally { setL(false); }
  };
  useEffect(() => { load();   }, [id]);

  useEffect(() => {
    let alive = true;
    api.get(`/datalab/persons/${id}/order-links`)
      .then((r) => {
        if (!alive) return;
        const map = {};
        for (const l of (r.data?.links || [])) map[String(l.number)] = l;
        setOrderLinks(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);

  const runPdSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post(`/pipedrive/sync/${id}`);
      toast.success("PD SYNC done", {
        description: r.derived?.tier
          ? `Tier: ${r.derived.tier} · Stage: ${r.derived.stage_key || "—"}`
          : "Stage/tier re-derived",
      });
      await load();
    } catch (e) {
      toast.error("PD SYNC failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  };

  if (loading) return <Layout><div className="text-center text-slate-500 py-20">Loading person…</div></Layout>;
  if (err || !doc) return <Layout><div className="max-w-xl mx-auto text-center py-20 text-red-400">{err || "Person not found"}</div></Layout>;

  const ltv    = Number(doc.TotalOrdersValue);
  const orders = Number(doc.NumberOfOrders);
  const avg    = orders > 0 ? ltv / orders : null;
  // Primary address = top-level flat fields. Addresses[] holds only ADDITIONAL
  // addresses (the whole array), never the primary.
  const addressLines = [doc.Address1, doc.Address2, doc.City, doc.State, doc.PostCode, doc.Country].filter(Boolean);
  const _hasAddrData = (a) => a && [a.address1, a.address2, a.city, a.state_or_province, a.postal_code, a.country].some(Boolean);
  const _allAddrs = (doc.Addresses || []).filter(_hasAddrData);
  const orderIds = Array.isArray(doc.OrderIds) ? doc.OrderIds : [];
  const names    = Array.isArray(doc.Names) ? doc.Names : [];
  const companies= Array.isArray(doc.Companies) ? doc.Companies : [];
  const notes    = Array.isArray(doc.Notes) ? doc.Notes : [];
  const activities = Array.isArray(doc.Activities) ? doc.Activities : [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="lab-person-detail-page">
        <BackLink to="/lab/persons" label="Data Lab · Persons" />

        {/* HEADER */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#0F1420] via-[#0F1420] to-[#1a1223] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {doc.LongVIPLevel && <Chip tone={TIER_TONE(doc.LongVIPLevel)}>★ {doc.LongVIPLevel}</Chip>}
                <PipedriveIdChip
                  pipelineId={doc.PipelineId}
                  stageId={doc.StageId}
                  shortVip={doc.ShortVIPLevel}
                  testid="lab-person-pd-chip"
                />
                {doc.DealId && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-purple-400/30 bg-purple-500/[0.06] text-purple-200 font-mono text-[10px] uppercase tracking-widest">
                    Deal #{doc.DealId}
                  </span>
                )}
                {doc.PersonId != null && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-slate-400 font-mono text-[10px] uppercase tracking-widest">
                    PD Person #{doc.PersonId}
                  </span>
                )}
                <button
                  type="button"
                  onClick={runPdSync}
                  disabled={syncing}
                  data-testid="lab-person-pd-sync"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-orange-500/40 bg-orange-500/[0.06] text-orange-200 hover:bg-orange-500/[0.14] font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
                  title="Recompute tier and stage from Mongo PipelineId + StageId + ShortVIPLevel"
                >
                  {syncing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
                  PD Sync
                </button>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight break-words">
                {asText(doc.FullName || doc.Email)}
              </h1>
              <div className="text-sm text-slate-300 mt-1">{asText(doc.DealTitle)}</div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 text-xs text-slate-400 font-mono">
                <span>Doc {asText(doc._id)}</span>
                {doc.UpdatedDate && <span>Updated {dateGB(doc.UpdatedDate)}</span>}
                {doc._received_at && <span>Synced {dateGB(doc._received_at)}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Lifetime value</div>
              <div className="text-4xl font-bold font-mono text-orange-400">{money(ltv)}</div>
              <div className="text-xs text-slate-400 mt-1 font-mono">
                {orders || 0} orders · avg {avg != null ? money(avg) : "—"}
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <KV label="# Orders (total)" value={<span className="font-mono">{asText(doc.NumberOfOrders)}</span>} />
            <KV label="Registered"       value={<span className="font-mono">{asText(doc.NumberOfRegOrders)}</span>} />
            <KV label="Guest"            value={<span className="font-mono">{asText(doc.NumberOfGuestOrders)}</span>} />
            <KV label="First order"      value={<span className="font-mono text-xs">{dateGB(doc.FirstOrderDate) || "—"}</span>} />
            <KV label="Last order"       value={<span className="font-mono text-xs">{dateGB(doc.LastOrderDate) || "—"}</span>} />
            <KV label="Avg order"        value={<span className="font-mono">{money(doc.AverageOrderValue)}</span>} />
          </div>
        </div>

        {/* Data Health strip + Controls + CRM/AI/Notes zones (Module 06) */}
        <PersonHealthZones doc={doc} onRefresh={load} refreshing={loading} />

        {/* 2-col layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact */}
            <Panel icon={UserIcon} title="Contact" testid="panel-contact">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <KV label="Full name"   value={<span className="text-white font-semibold">{asText(doc.FullName)}</span>} />
                <KV label="First name"  value={asText(doc.FirstName)} />
                <KV label="Last name"   value={asText(doc.LastName)} />
                <KV label="Company"     value={asText(doc.Company)} />
                <KV label="Email"       value={<a href={`mailto:${doc.Email}`} className="font-mono text-[#C8A46B] break-all hover:underline">{asText(doc.Email)}</a>} wide />
                <KV label="Phone"       value={<a href={`tel:${String(doc.Phone || "").replace(/\s/g,"")}`} className="font-mono text-slate-100 hover:text-white">{asText(doc.Phone)}</a>} />
                <KV label="BC group"    value={<span className="font-mono">{asText(doc.BCCustGroupId)}</span>} />
              </div>
            </Panel>

            {/* Addresses */}
            <Panel
              icon={MapPin}
              title="Addresses"
              right={_allAddrs.length > 0 ? `${_allAddrs.length} additional address${_allAddrs.length === 1 ? "" : "es"} on file` : null}
              testid="panel-address"
            >
              {addressLines.length === 0 ? (
                <div className="text-sm text-slate-500">No primary address on file.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <KV label="Line 1"   value={asText(doc.Address1)} wide />
                  <KV label="Line 2"   value={asText(doc.Address2)} wide />
                  <KV label="City"     value={asText(doc.City)} />
                  <KV label="State"    value={asText(doc.State)} />
                  <KV label="Post code" value={<span className="font-mono text-[#C8A46B]">{asText(doc.PostCode)}</span>} />
                  <KV label="Country"  value={asText(doc.Country)} />
                </div>
              )}

              {_allAddrs.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAddrs((v) => !v)}
                    data-testid="toggle-all-addresses"
                    aria-expanded={showAddrs}
                    className="mt-4 inline-flex items-center gap-1.5 px-2 py-1 rounded border border-white/[0.14] bg-white/[0.03] hover:bg-white/[0.08] text-[10px] font-mono uppercase tracking-widest text-slate-300 transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    {showAddrs
                      ? `Hide additional addresses (${_allAddrs.length})`
                      : `Show additional addresses (${_allAddrs.length})`}
                  </button>
                  {showAddrs && (
                    <div className="mt-3 space-y-3" data-testid="all-addresses">
                      {_allAddrs.map((addr, i) => (
                        <ExtraAddress key={i} addr={addr} index={i + 1} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* Pipedrive linkage */}
            <Panel icon={GitBranch} title="Pipedrive linkage" right={<PipedriveIdChip pipelineId={doc.PipelineId} stageId={doc.StageId} shortVip={doc.ShortVIPLevel} size="xs" />} testid="panel-pipedrive">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KV label="Pipeline ID"   value={<span className="font-mono text-teal-300 font-semibold">{asText(doc.PipelineId)}</span>} />
                <KV label="Stage ID"      value={<span className="font-mono text-teal-300 font-semibold">{asText(doc.StageId)}</span>} />
                <KV label="Person ID"     value={<span className="font-mono">{asText(doc.PersonId)}</span>} />
                <KV label="Deal ID"       value={<span className="font-mono">{asText(doc.DealId)}</span>} />
                <KV label="Deal title"    value={asText(doc.DealTitle)} wide />
                <KV label="Org ID"        value={<span className="font-mono">{asText(doc.OrgId)}</span>} />
                <KV label="Short VIP"     value={<span className="font-mono">{asText(doc.ShortVIPLevel)}</span>} />
                <KV label="Long VIP"      value={<span className="font-mono">{asText(doc.LongVIPLevel)}</span>} />
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 flex-wrap">
                <Link to={`/crm/persons/${doc._id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-white/10 bg-white/[0.03] text-sm text-slate-200 hover:text-white hover:bg-white/[0.06]" data-testid="lab-person-open-360">
                  <UserIcon className="w-3.5 h-3.5" /> Open Customer 360° <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to={`/deals`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-white/10 bg-white/[0.03] text-sm text-slate-200 hover:text-white hover:bg-white/[0.06]">
                  <GitBranch className="w-3.5 h-3.5" /> View in pipeline <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </Panel>

            {/* BC linkage / Order IDs */}
            <Panel icon={ShoppingBag} title="BigCommerce linkage" right={`${orderIds.length} orders · ${(doc.BCCustIds || []).length} customer IDs`} testid="panel-bc">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <KV label="BC Cust IDs" value={
                  (doc.BCCustIds || []).length === 0
                    ? <span className="text-slate-500 text-xs">None linked</span>
                    : <div className="flex flex-wrap gap-1">
                        {(doc.BCCustIds || []).map((cid) => (
                          <Link key={cid} to={`/lab/customers/${cid}`} className="font-mono text-xs px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-[#C8A46B] hover:text-white hover:bg-white/[0.06]">
                            #{cid}
                          </Link>
                        ))}
                      </div>
                } wide />
              </div>
              {orderIds.length === 0 ? (
                <div className="text-sm text-slate-500">No orders linked to this person yet.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {orderIds.map((oid) => {
                    const link = orderLinks[String(oid)];
                    const resolved = link ? link.resolved : null;
                    return (
                      <Link
                        key={oid}
                        to={`/lab/orders/${oid}`}
                        data-testid={`lab-person-orderid-${oid}`}
                        title={resolved === false ? "Not in Talmia 2 yet" : (resolved ? "Opens the order" : "")}
                        className={`inline-flex items-center gap-1 font-mono text-xs px-2 py-1 rounded border ${
                          resolved === false
                            ? "border-amber-500/30 bg-amber-500/[0.06] text-amber-300/80"
                            : "border-white/10 bg-white/[0.02] text-slate-200 hover:text-white hover:bg-white/[0.06]"
                        }`}
                      >
                        #{oid}
                        {resolved === false
                          ? <span className="text-[9px] uppercase tracking-widest ml-0.5">not in T2</span>
                          : <ExternalLink className="w-2.5 h-2.5" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </Panel>

            <RawJson data={doc} title="Raw document" testid="panel-raw" />
          </div>

          {/* RIGHT (sidebar) */}
          <div className="space-y-6">
            {/* Purchase behaviour */}
            <Panel icon={TrendingUp} title="Purchase behaviour" testid="panel-behaviour">
              <div className="grid grid-cols-1 gap-4">
                <KV label="Lifetime value"  value={<span className="font-mono text-[#C8A46B]">{money(doc.TotalOrdersValue)}</span>} />
                <KV label="Total orders"    value={<span className="font-mono">{asText(doc.NumberOfOrders)}</span>} />
                <KV label="Registered"      value={<span className="font-mono">{asText(doc.NumberOfRegOrders)}</span>} />
                <KV label="Guest"           value={<span className="font-mono">{asText(doc.NumberOfGuestOrders)}</span>} />
                <KV label="Average order"   value={<span className="font-mono">{money(doc.AverageOrderValue)}</span>} />
                <KV label="First order"     value={<span className="font-mono">{dateGB(doc.FirstOrderDate)}</span>} />
                <KV label="Last order"      value={<span className="font-mono">{dateGB(doc.LastOrderDate)}</span>} />
              </div>
            </Panel>

            {/* Alternative names */}
            {(names.length > 0 || companies.length > 0) && (
              <Panel icon={Users2} title="Aliases & companies" right={`${names.length} names · ${companies.length} orgs`} testid="panel-aliases">
                {names.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Names on record</div>
                    <div className="flex flex-wrap gap-1">
                      {names.map((n, i) => (
                        <span key={`n-${i}-${n}`} className="text-xs px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-slate-200">{n}</span>
                      ))}
                    </div>
                  </div>
                )}
                {companies.length > 0 && (
                  <div className={names.length > 0 ? "mt-3 pt-3 border-t border-white/5" : ""}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Companies</div>
                    <div className="flex flex-wrap gap-1">
                      {companies.map((c, i) => (
                        <span key={`c-${i}-${c}`} className="text-xs px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-slate-200">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </Panel>
            )}

            {/* Activity + notes */}
            {(activities.length > 0 || notes.length > 0) && (
              <Panel icon={ActivityIcon} title="Recent activity" right={`${activities.length} activities · ${notes.length} notes`} testid="panel-activity">
                {activities.length === 0 && <div className="text-slate-500 text-xs">No activity yet.</div>}
                {activities.slice(0, 8).map((a, i) => (
                  <div key={`a-${i}-${a?.id ?? a?.type ?? i}`} className="text-xs text-slate-300 border-b border-white/5 py-1.5 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-200">{a.type || a.subject || "activity"}</span>
                      <span className="font-mono text-[10px] text-slate-500">{dateGB(a.add_time || a.due_date)}</span>
                    </div>
                    {a.note && <div className="mt-0.5 text-slate-400 truncate">{a.note}</div>}
                  </div>
                ))}
              </Panel>
            )}

            {notes.length > 0 && (
              <Panel icon={StickyNote} title="Notes" right="see Intelligence · Notes above" testid="panel-notes">
                <div className="text-[12px] text-slate-500">
                  {notes.length} raw note entr{notes.length === 1 ? "y" : "ies"} — rendered deduped, tagged and contamination-flagged in the <span className="text-[#C8A46B]">Intelligence · Notes</span> zone at the top.
                </div>
              </Panel>
            )}
          </div>
        </div>
      </div>

      <LinkedRecords collection="persons" id={id} />
      <DataAudit doc={doc} title="Data audit — full record" testid="lab-data-audit" />
      <RecordHistory collection="persons" docId={id} testid="lab-person-history" />
    </Layout>
  );
}


// One address from the person's Addresses[] array. Reads every field from the
// array item itself — NOT the top-level doc — so each row shows its own
// city/postcode/etc. Matches the primary KV format.
function ExtraAddress({ addr, index }) {
  const typeLabel = asText(addr.address_type) !== "—" ? addr.address_type : null;
  const label = typeLabel ? `Address ${index} · ${typeLabel}` : `Address ${index}`;
  return (
    <div className="pt-3 border-t border-white/[0.08]" data-testid={`address-${index}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#C8A46B]/80 font-mono mb-2">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KV label="Line 1"    value={asText(addr.address1)} wide />
        <KV label="Line 2"    value={asText(addr.address2)} wide />
        <KV label="City"      value={asText(addr.city)} />
        <KV label="State"     value={asText(addr.state_or_province)} />
        <KV label="Post code" value={<span className="font-mono text-[#C8A46B]">{asText(addr.postal_code)}</span>} />
        <KV label="Country"   value={asText(addr.country)} />
        {addr.company && <KV label="Company" value={asText(addr.company)} />}
        {addr.phone && <KV label="Phone" value={asText(addr.phone)} />}
      </div>
    </div>
  );
}
